import React, { useState, useCallback } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { AccentPicker } from '../shell/AccentPicker';

interface LoginProps {
  onSwitchToRegister: () => void;
  onForgotPassword: () => void;
}

function InfoIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

function WarningIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

export function Login({ onSwitchToRegister, onForgotPassword }: LoginProps) {
  const { login, demoLogin, isLoading, error, clearError } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setLocalError(null);
      clearError();

      try {
        await login(email, password);
        // Auth state updated; AppShell will react and redirect to app.
      } catch (err) {
        // Error is set in store
      }
    },
    [email, password, login, clearError]
  );

  const handleDemoLogin = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    clearError();
    setLocalError(null);
    demoLogin();
  }, [clearError, demoLogin]);

  const displayError = localError ?? error;

  return (
    <div
      className="flex flex-col items-center justify-center min-h-full px-4"
      style={{ backgroundColor: 'var(--bg-base)' }}
    >
      <div
        className="w-full max-w-md p-8 rounded-lg"
        style={{
          backgroundColor: 'var(--bg-surface)',
          border: '1px solid var(--border)',
        }}
      >
        {/* Logo / Title */}
        <div className="text-center mb-8">
          <h1
            className="text-2xl font-semibold tracking-tight mb-1"
            style={{ color: 'var(--text-primary)', fontFamily: 'DM Sans, system-ui, sans-serif' }}
          >
            Welcome back
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Sign in to your Signa account
          </p>
        </div>

        {/* Main color */}
        <div
          className="mb-6 flex items-center justify-center gap-3 rounded-lg px-3 py-2"
          style={{
            backgroundColor: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            color: 'var(--text-secondary)',
          }}
        >
          <span className="text-xs font-medium uppercase tracking-wider" style={{ fontFamily: 'var(--font-mono)' }}>
            Main color
          </span>
          <AccentPicker />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className="block text-xs font-medium mb-1.5 uppercase tracking-wider"
              style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="w-full px-3 py-2.5 rounded-lg text-sm outline-none transition-colors"
              style={{
                backgroundColor: 'var(--bg-elevated)',
                border: '1px solid var(--border)',
                color: 'var(--text-primary)',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
            />
          </div>

          {/* Password */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label
                htmlFor="password"
                className="block text-xs font-medium uppercase tracking-wider"
                style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}
              >
                Master Password
              </label>
              <button
                type="button"
                onClick={onForgotPassword}
                className="text-xs transition-colors"
                style={{ color: 'var(--text-tertiary)' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--accent)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-tertiary)'; }}
              >
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full px-3 py-2.5 pr-10 rounded-lg text-sm outline-none transition-colors"
                style={{
                  backgroundColor: 'var(--bg-elevated)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-primary)',
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs"
                style={{ color: 'var(--text-tertiary)' }}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  ) : (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  )}
              </button>
            </div>
          </div>

          {/* Error */}
          {displayError && (
            <div
              className="p-3 rounded-lg text-sm"
              style={{
                backgroundColor: 'color-mix(in srgb, var(--error) 10%, transparent)',
                border: '1px solid var(--error)',
                color: 'var(--error)',
              }}
            >
              {displayError}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            style={{
              backgroundColor: 'var(--accent)',
              color: 'var(--text-inverse)',
            }}
            onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = 'var(--accent-strong)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent)'; }}
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        {/* Authentication note */}
        <div
          className="mt-5 p-3 rounded-lg text-xs leading-relaxed flex gap-2"
          style={{
            backgroundColor: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            color: 'var(--text-secondary)',
          }}
        >
          <span className="mt-0.5 shrink-0" style={{ color: 'var(--accent)' }}>
            <InfoIcon />
          </span>
          <span>
            Google and Microsoft mailbox connections happen after sign-in. Your Signa account
            is protected by your master password.
          </span>
        </div>

        {/* Forgot password warning */}
        <div
          className="mt-4 p-3 rounded-lg text-xs leading-relaxed flex gap-2"
          style={{
            backgroundColor: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            color: 'var(--text-secondary)',
          }}
        >
          <span className="mt-0.5 shrink-0" style={{ color: 'var(--accent)' }}>
            <InfoIcon />
          </span>
          <span>
            If you forgot your master password, your data cannot be recovered.
            There is no account recovery; this is by design for maximum security.
          </span>
        </div>

        {/* Footer */}
        <p className="text-center text-sm mt-6" style={{ color: 'var(--text-secondary)' }}>
          Don't have an account?{' '}
          <button
            type="button"
            onClick={onSwitchToRegister}
            className="font-medium underline underline-offset-2"
            style={{ color: 'var(--accent)' }}
          >
            Create one
          </button>
        </p>

        {/* Demo mode; no API needed */}
        <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
          <p className="text-xs text-center mb-2 uppercase tracking-wider" style={{ color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>
            No account needed
          </p>
          <button
            type="button"
            onClick={handleDemoLogin}
            className="text-sm px-4 py-2.5 rounded-lg w-full font-medium transition-colors"
            style={{ backgroundColor: 'var(--accent)', color: 'var(--text-inverse)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent-strong)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent)'; }}
          >
            Try Demo - explore the UI
          </button>
          <p className="text-xs text-center mt-2" style={{ color: 'var(--text-tertiary)' }}>
            No signup required - instant access
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Forgot password modal; warns about data loss.
 */
export function ForgotPasswordModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 flex items-center justify-center px-4 z-50"
      style={{ backgroundColor: 'color-mix(in srgb, var(--bg-base) 72%, transparent)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-sm p-6 rounded-lg"
        style={{
          backgroundColor: 'var(--bg-surface)',
          border: '1px solid var(--border)',
        }}
      >
        <h2
          className="text-lg font-semibold mb-3"
          style={{ color: 'var(--text-primary)' }}
        >
          Password cannot be recovered
        </h2>
        <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
          Signa uses zero-knowledge encryption. Your master password is never stored;
          it's only used to derive your encryption key.
        </p>
        <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
          If you forget your password, your data (emails, notes, tasks) will be
          permanently inaccessible. There is no recovery path.
        </p>
        <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
          <strong style={{ color: 'var(--warning)' }}>If you still remember your password,</strong>{' '}
          just sign in above.
        </p>
        <div
          className="p-3 rounded-lg text-xs mb-4 flex gap-2"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--error) 7%, transparent)',
            border: '1px solid var(--error)',
            color: 'var(--error)',
          }}
        >
          <span className="mt-0.5 shrink-0">
            <WarningIcon />
          </span>
          <span>
            There is no "forgot password" flow by design. Write down your password
            and store it safely.
          </span>
        </div>
        <button
          onClick={onClose}
          className="w-full py-2 rounded-lg text-sm font-medium"
          style={{
            backgroundColor: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            color: 'var(--text-primary)',
          }}
        >
          I understand
        </button>
      </div>
    </div>
  );
}
